import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { enqueueCommand } from 'Common/Components/FileUploadContext/actions';
import CVEActionMenu from 'Common/Components/CVEActionMenu';
import {
  extractCommandIdFromResponse,
  hasCveListChanged,
  isWorkspaceGroupedByField,
  shouldTreatFieldUpdateAsGrouping
} from 'Common/Functions/vulnerabilityGrouping';
import { extractIsGroupingFromResponse } from 'Common/Functions/vulnerabilityNameEdit';
import {
  Wrapper,
  DataContainer,
  DataItem,
  InputComp,
  Trash,
  TrashWrapper,
  Error,
  InputWrapper,
  ErrorIcon,
  Plus,
  Placeholder,
  ReadOnlyInputButton,
  PlusContainer
} from './styled';
import { Title } from '../ExpandableWrapper/styled';
import { selectAllowedByRole } from 'Hooks/usePermission/selector';
import { FEATURE_VULNS, PERMISSION_TYPES } from 'Hooks/usePermission/permissions';
import {
  selectCurrentWorkspace,
  selectIsReadOnly,
  selectWorkspaces
} from 'store/Faraday/selectors';
import EditVulnerabilityCVEModal from './components/EditVulnerabilityCVEModal';

const CVE = ({ showRiskInfo, currentVuln, updateVuln }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const allowedByRole = useSelector((state) => selectAllowedByRole(state, FEATURE_VULNS, PERMISSION_TYPES.UPDATE));
  const readOnly = useSelector(selectIsReadOnly);
  const workspaces = useSelector(selectWorkspaces);
  const currentWorkspace = useSelector(selectCurrentWorkspace);
  const canUpdate = !readOnly && allowedByRole;
  const workspaceName = currentVuln?.workspace_name || currentWorkspace;
  const isGroupedByCve = isWorkspaceGroupedByField(workspaceName, workspaces, 'cve');
  const [list, setList] = useState(currentVuln?.cve || []);
  const [showEditModal, setShowEditModal] = useState(false);
  const [pendingUpdates, setPendingUpdates] = useState(0);
  const commandIdRef = useRef(null);
  const isGroupingRef = useRef(false);
  const groupingStartTimestampRef = useRef(null);
  const initialListRef = useRef([]);

  useEffect(() => {
    setList(currentVuln?.cve || []);
  }, [currentVuln?._id, currentVuln?.cve]);

  const persistCveList = useCallback(async (nextList) => {
    const previousList = list;
    const groupingStartTimestamp = Date.now();
    setList(nextList);
    setPendingUpdates((value) => value + 1);

    try {
      const result = await dispatch(updateVuln(currentVuln, 'cve', nextList, { enqueueGroupingFromResponse: false }));
      if (!result?.ok) {
        setList(previousList);
        return result;
      }

      const commandId = result.commandId || extractCommandIdFromResponse(result.response);
      const isGrouping = shouldTreatFieldUpdateAsGrouping({
        workspaceName,
        workspaces,
        field: 'cve',
        commandId,
        isGrouping: result?.isGrouping ?? (result?.response ? extractIsGroupingFromResponse(result.response) === true : false)
      });
      commandIdRef.current = commandId ?? null;
      isGroupingRef.current = isGrouping === true;
      groupingStartTimestampRef.current = isGrouping === true ? groupingStartTimestamp : null;
      return result;
    } finally {
      setPendingUpdates((value) => Math.max(0, value - 1));
    }
  }, [currentVuln, dispatch, list, updateVuln, workspaceName, workspaces]);

  const onAdd = useCallback((name) => {
    const nextList = [...list, name];
    return persistCveList(nextList);
  }, [list, persistCveList]);

  const onRemove = useCallback((name) => {
    const nextList = list.filter((item) => item !== name);
    return persistCveList(nextList);
  }, [list, persistCveList]);

  const handleOpenModal = useCallback((event) => {
    if (event && typeof event.preventDefault === 'function') event.preventDefault();
    if (event && typeof event.stopPropagation === 'function') event.stopPropagation();

    if (!canUpdate) return;
    initialListRef.current = list;
    commandIdRef.current = null;
    isGroupingRef.current = false;
    groupingStartTimestampRef.current = null;
    setShowEditModal(true);
  }, [canUpdate, list]);

  const handleCloseModal = useCallback(() => {
    setShowEditModal(false);
    commandIdRef.current = null;
    isGroupingRef.current = false;
    groupingStartTimestampRef.current = null;
  }, []);

  const handleSaveModal = useCallback(() => {
    const changed = hasCveListChanged(initialListRef.current, list);

    if (isGroupedByCve && changed && isGroupingRef.current) {
      dispatch(enqueueCommand({
        command_id: commandIdRef.current ?? null,
        commandType: 'grouping',
        name: workspaceName,
        fileType: 'grouping',
        isGrouping: true,
        groupingStartTimestamp: groupingStartTimestampRef.current ?? Date.now(),
        error: false,
        finished: false
      }));
    }

    handleCloseModal();
  }, [dispatch, handleCloseModal, isGroupedByCve, list, workspaceName]);

  const title = intl.formatMessage({ id: 'manage.detail.tab.general.cve' });
  const groupedView = (
    <>
      {canUpdate && (
        <InputWrapper>
          <ReadOnlyInputButton type="button" onClick={ handleOpenModal }>
            CVE-YYYY-NNNN
          </ReadOnlyInputButton>
          <Plus onClick={ handleOpenModal } />
        </InputWrapper>
      )}
      <DataContainer showGradient={ list.length < 3 }>
        {list.length === 0 && (
          <DataItem isOdd={ false } onClick={ canUpdate ? handleOpenModal : undefined }>
            <Placeholder as="span">
              {canUpdate ? 'No CVEs added. Click to edit.' : 'No CVEs added.'}
            </Placeholder>
          </DataItem>
        )}
        {list.map((name, i) => (
          <DataItem
            key={ `cve_${name}_${i}` }
            isOdd={ i % 2 !== 0 }
          >
            <CVEActionMenu
              cve={ name }
              onEdit={ handleOpenModal }
              canEdit={ canUpdate }
              variant="detail"
              fullWidth
            />
          </DataItem>
        ))}
      </DataContainer>
      <EditVulnerabilityCVEModal
        show={ showEditModal }
        onClose={ handleCloseModal }
        onSave={ handleSaveModal }
        showGroupingWarning={ isGroupedByCve }
        saveDisabled={ pendingUpdates > 0 }
        editor={ (
          <Input
            placeholder="CVE-YYYY-NNNN"
            errorMessage="CVE not valid"
            validator={ /^(CVE-(1999|2\d{3})-(\d{4,7}))$/ }
            canUpdate={ canUpdate }
            onAdd={ onAdd }
            onRemove={ onRemove }
            listData={ list }
            useActionMenu
          />
        ) }
      />
    </>
  );

  return (
    <Wrapper>
      { showRiskInfo ? <Title children={ title } /> : null }
      {groupedView}
    </Wrapper>
  );
};

export default CVE;

export const Input = ({
  validator,
  placeholder,
  errorMessage,
  canUpdate,
  listData,
  onAdd,
  onRemove,
  expanded,
  useActionMenu,
  validateOnChange,
  onValidationChange,
  inputTestId,
  chipTestId,
  removeTestId
}) => {
  const [value, setValue] = useState('');
  const [error, setError] = useState(false);
  const isOdd = (n) => n % 2 !== 0;

  const isValid = (value) => {
    if (!validator) return true;
    return validator.test(value);
  };

  const addCVE = async () => {
    const valueToValidate = value.trim().toUpperCase();
    if (valueToValidate === '') {
      setError(false);
      onValidationChange(false);
      return;
    }

    if (!isValid(valueToValidate)) {
      setError(true);
      onValidationChange(true);
      return;
    }
    await onAdd(valueToValidate);
    setValue('');
    setError(false);
    onValidationChange(false);
  };
  const handleKeyPress = async (e) => {
    if (e.key === 'Enter') {
      await addCVE();
    }
  };

  const handleChange = (e) => {
    const nextValue = e.target.value;
    setValue(nextValue);

    if (validateOnChange) {
      const nextError = nextValue.trim() !== '' && !isValid(nextValue.trim().toUpperCase());
      setError(nextError);
      onValidationChange(nextError);
    } else {
      setError(false);
    }
  };

  return (
    <>
      { canUpdate && <InputWrapper>
        <InputComp
          value={ value }
          placeholder={ placeholder }
          onChange={ handleChange }
          onKeyPress={ handleKeyPress }
          disabled={ !canUpdate }
          data-testid={ inputTestId || undefined }
        />
        <PlusContainer>
          <Plus onClick={ addCVE } />
        </PlusContainer>
        {error && (
        <Error>
          <ErrorIcon />
          {errorMessage}
        </Error>
        )}
                     </InputWrapper> }
      <DataContainer showGradient={ listData.length < 3 } expanded={ expanded }>
        { listData && listData.map((name, i) => (
          <DataItem
            key={ `cve_item_${name}_${i}` }
            isOdd={ isOdd(i) }
            data-testid={ chipTestId || undefined }
            data-chip-index={ i }
          >
            {useActionMenu && (
              <CVEActionMenu
                cve={ name }
                canEdit={ false }
                variant="detail"
                fullWidth
              />
            )}
            {!useActionMenu && (
              <Placeholder href={ `https://www.cve.org/CVERecord?id=${name.toUpperCase()}` } target="_blank" rel="noopener noreferrer">{name}</Placeholder>
            )}
            { canUpdate && <TrashWrapper
              onClick={ (e) => {
                e.stopPropagation();
                onRemove(name);
              } }
              data-testid={ removeTestId || undefined }
              data-chip-index={ i }
            >
              <Trash />
                           </TrashWrapper> }
          </DataItem>
        )) }
      </DataContainer>
    </>
  );
};

CVE.propTypes = {
  showRiskInfo: PropTypes.bool,
  currentVuln: PropTypes.shape({
    _id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    cve: PropTypes.arrayOf(PropTypes.string),
    workspace_name: PropTypes.string
  }),
  updateVuln: PropTypes.func.isRequired
};

CVE.defaultProps = {
  showRiskInfo: false,
  currentVuln: {
    cve: []
  }
};

Input.propTypes = {
  validator: PropTypes.instanceOf(RegExp),
  placeholder: PropTypes.string,
  errorMessage: PropTypes.string,
  canUpdate: PropTypes.bool,
  listData: PropTypes.arrayOf(PropTypes.string),
  onAdd: PropTypes.func,
  onRemove: PropTypes.func,
  expanded: PropTypes.bool,
  useActionMenu: PropTypes.bool,
  validateOnChange: PropTypes.bool,
  onValidationChange: PropTypes.func,
  inputTestId: PropTypes.string,
  chipTestId: PropTypes.string,
  removeTestId: PropTypes.string
};

Input.defaultProps = {
  validator: null,
  placeholder: '',
  errorMessage: '',
  canUpdate: false,
  listData: [],
  onAdd: async () => {},
  onRemove: async () => {},
  expanded: false,
  useActionMenu: false,
  validateOnChange: false,
  onValidationChange: () => {},
  inputTestId: '',
  chipTestId: '',
  removeTestId: ''
};
